import React from "react";
import { Link } from "react-router-dom";
import {
    Box,
    Typography,
    Grid,
    Card,
    CardContent,
    CardMedia,
    Button,
    Chip,
    styled,
} from "@mui/material";

import CodeIcon from "@mui/icons-material/Code";
import PersonalVideoIcon from "@mui/icons-material/PersonalVideo";
import RadioOption from "../../../components/RadioOption";

/* ============================================
   PREMIUM OPTION‑A BACKGROUND
============================================ */
const bgImage =
    "https://images.unsplash.com/photo-1499951360447-b19be8fe80f5";

/* ============================================
   STYLED COMPONENTS
============================================ */
const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundImage: `linear-gradient(rgba(16,7,32,0.90), rgba(16,7,32,0.95)), url(${bgImage})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
    paddingTop: "120px",
    paddingBottom: "80px",
    color: "white",
}));

const GlassCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(255,255,255,0.85)",
    backdropFilter: "blur(10px)",
    borderRadius: "14px",
    padding: "20px",
    boxShadow: "0 4px 14px rgba(0,0,0,0.25)",
}));

const NavTab = styled(Chip)(({ theme }) => ({
    px: 2.5,
    py: 1,
    fontWeight: 700,
    fontSize: "0.9rem",
    borderRadius: "999px",
    cursor: "pointer",
}));

/* ============================================
   LANGUAGE TABS
============================================ */
const languageTabs = [
    { label: "TypeScript", to: "/typescript-dashboard" },
    { label: "JavaScript", to: "/javascriptcoding" },
    { label: "Python", to: "/pythonCodingDashBoard" },
    { label: "C++", to: "/cpp-dashboard" },
    { label: "Express.js", to: "/express-dashboard" },
    { label: "Spring Boot", to: "/springboot-dashboard" },
    { label: "Git & GitHub", to: "/git-dashboard" },
];

/* ============================================
   GIT TOPICS
============================================ */
const gitTopics = [
    { title: "Introduction to Git & GitHub", link: "/git-intro" },
    { title: "Installing Git & Setup", link: "/git-setup" },
    { title: "Git Basics (init, add, commit)", link: "/git-basics" },
    { title: "Branching & Merging", link: "/git-branching" },
    { title: "Git Log & History", link: "/git-log-history" },
    { title: "Undoing Changes", link: "/git-undo" },
    { title: "Working with Remotes", link: "/git-remotes" },
    { title: "GitHub Repositories", link: "/github-repos" },
    { title: "Pull Requests & Collaboration", link: "/github-prs" },
    { title: "Git Ignore & Cleanups", link: "/git-ignore" },
    { title: "Git Tags & Releases", link: "/git-tags" },
    { title: "Git Stash & Cherry-Pick", link: "/git-stash-cherry-pick" },
    { title: "Git Rebase vs Merge", link: "/git-rebase-merge" },
    { title: "Git Hooks", link: "/git-hooks" },
    { title: "GitHub Actions (CI/CD)", link: "/github-actions" },
    { title: "Git Submodules", link: "/git-submodules" },
    { title: "Git Security & GPG", link: "/git-security" },
    { title: "Troubleshooting Git", link: "/git-troubleshooting" },
];

export default function GitDashboard() {
    return (
        <PageWrapper>
            {/* ============================================
          LANGUAGE TABS
      ============================================ */}
            <Box
                sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    justifyContent: "center",
                    gap: 2,
                    px: 3,
                    pb: 3,
                }}
            >
                {languageTabs.map((tab) => (
                    <Link key={tab.to} to={tab.to} style={{ textDecoration: "none" }}>
                        <NavTab
                            label={tab.label}
                            sx={{
                                backgroundColor:
                                    tab.label === "Git & GitHub"
                                        ? "goldenrod"
                                        : "rgba(255,255,255,0.12)",
                                color: tab.label === "Git & GitHub" ? "#100720" : "white",
                                "&:hover": {
                                    backgroundColor:
                                        tab.label === "Git & GitHub"
                                            ? "#e0b200"
                                            : "rgba(255,255,255,0.25)",
                                },
                            }}
                        />
                    </Link>
                ))}
            </Box>

            {/* ============================================
          GOLDEN BANNER TITLE
      ============================================ */}
            <Box sx={{ textAlign: "center", mb: 6 }}>
                <Typography
                    variant="h3"
                    sx={{
                        fontWeight: 800,
                        backgroundColor: "goldenrod",
                        color: "#100720",
                        px: 4,
                        py: 1,
                        borderRadius: "999px",
                        display: "inline-block",
                    }}
                >
                    Git & GitHub Mastery
                </Typography>
            </Box>

            {/* ============================================
          MAIN CONTENT GRID
      ============================================ */}
            <Grid container spacing={4} sx={{ px: { xs: 2, md: 6 } }}>
                {/* LEFT COLUMN — OVERVIEW */}
                <Grid item xs={12} md={5}>
                    <GlassCard>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: 700, color: "#100720", textAlign: "center" }}
                        >
                            Git Fundamentals
                        </Typography>

                        <Card
                            sx={{
                                width: "100%",
                                backgroundColor: "lightgray",
                                textAlign: "center",
                                padding: "15px",
                                borderTop: "2px solid white",
                                mt: 2,
                            }}
                        >
                            <CardMedia
                                component="img"
                                height="220"
                                image="https://git-scm.com/images/logos/downloads/Git-Icon-1788C.png"
                                alt="Git Logo"
                            />

                            <CardContent>
                                <Typography
                                    variant="h6"
                                    sx={{ fontWeight: 700, mb: 1, color: "#100720" }}
                                >
                                    Learn Version Control & Collaboration
                                </Typography>

                                <Typography variant="body1" sx={{ color: "#100720" }}>
                                    <PersonalVideoIcon sx={{ mr: 1 }} />
                                    Hands‑on Git & GitHub Skills
                                </Typography>

                                <Box mt={2}>
                                    <Link to="/compiler" style={{ textDecoration: "none" }}>
                                        <Button
                                            fullWidth
                                            variant="contained"
                                            sx={{
                                                backgroundColor: "#100720",
                                                color: "white",
                                                fontWeight: "bold",
                                                "&:hover": { backgroundColor: "#1a0a3d" },
                                            }}
                                            startIcon={<CodeIcon />}
                                        >
                                            Practice Git Commands
                                        </Button>
                                    </Link>
                                </Box>
                            </CardContent>
                        </Card>
                    </GlassCard>
                </Grid>

                {/* RIGHT COLUMN — TOPICS */}
                <Grid item xs={12} md={7}>
                    <GlassCard>
                        <Typography
                            variant="h5"
                            sx={{ fontWeight: 700, color: "#100720", textAlign: "center" }}
                        >
                            Explore Git Topics
                        </Typography>

                        <Box sx={{ mt: 3 }}>
                            {gitTopics.map((topic, i) => (
                                <Link
                                    key={i}
                                    to={topic.link}
                                    style={{ textDecoration: "none" }}
                                >
                                    <GlassCard
                                        sx={{
                                            mb: 2,
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            padding: "15px",
                                        }}
                                    >
                                        <Typography
                                            variant="h6"
                                            sx={{ fontWeight: 700, color: "#100720" }}
                                        >
                                            <RadioOption /> {topic.title}
                                        </Typography>

                                        <Button
                                            variant="contained"
                                            sx={{
                                                backgroundColor: "green",
                                                color: "white",
                                                fontWeight: "bold",
                                                borderRadius: "6px",
                                                "&:hover": { backgroundColor: "#0f7d0f" },
                                            }}
                                        >
                                            View
                                        </Button>
                                    </GlassCard>
                                </Link>
                            ))}
                        </Box>
                    </GlassCard>
                </Grid>
            </Grid>
        </PageWrapper>
    );
}
